import type { LinkableTextFrame } from '@/lib/text-linking/types/linkable-text-frame'
import { linkingChromeRectOfFrame } from '@/lib/text-linking/linkable-text-frame'
import { MIN_CHROME_SIZE_PX } from '@/components/app/editor/selection-chrome/selection-chrome-constants'
import { chromeGeometry } from '@/lib/shapes/selection-chrome-preview'
import type { PortPoint } from '@/lib/text-linking/text-linking-geometry'

/** A linkable frame's visual box in screen pixels, plus the pivot its rotation turns about. */
export type ChromeFrame = {
  x: number
  y: number
  width: number
  height: number
  rotation?: number
} & {
  pivot: PortPoint
}

/**
 * `inPortPoint`/`outPortPoint` take a plain `{x,y,width,height,rotation}` frame; a shape-backed
 * frame's ports must sit on its *visual* box (with rotation still pivoting on the shape's geometric
 * center) rather than `frame`'s own geometric bounds — see `linkingChromeRectOfFrame`. Every port/
 * connector call site in this layer goes through this helper instead of reading
 * `frame.x/y/width/height` directly, so none of them can regress back to the geometric box.
 *
 * Returns the frame in **screen pixels** — `scale` is applied here, once, the same way
 * `chromeGeometry` projects selection chrome. Everything downstream (port centers, connector
 * endpoints, hit-rect sizes) is then a literal on-screen measurement with nothing left to
 * counter-scale, matching how the resize anchors
 * (`visual-layer/selection-chrome-layer/components/chrome-resize-dots.tsx`) work. This layer
 * used to live *inside* the canvas's own `transform: scale()` wrapper and counter-scale every port
 * back down (`invScale`); it is now a sibling of that wrapper, same as the visual layer, and
 * `chromeGeometry` is the single place doc space turns into screen space.
 *
 * The minimum-size floor (`MIN_CHROME_SIZE_PX`, WMPCM-9199) is applied by `chromeGeometry` itself,
 * in screen px directly — no more doc-px round trip. The chrome grows a frame smaller than that
 * floor about its centre so two handle hit targets can't overlap; below that size the n/s resize
 * handles used to drift away from the in/out ports as the user zoomed out, because only the chrome
 * applied the floor. Both now apply the identical floor the identical way.
 */
export function chromeFrameOf(frame: LinkableTextFrame, scale: number): ChromeFrame {
  const { rect, pivot } = linkingChromeRectOfFrame(frame)
  const geometry = chromeGeometry(
    { rect, pivot, rotationDeg: frame.rotation ?? 0 },
    { scale, minScreenPx: MIN_CHROME_SIZE_PX }
  )

  return { ...geometry.rect, rotation: frame.rotation, pivot: geometry.pivot }
}
